import * as React from "react";
import {
  Typography,
  Button,
  TextField,
  Checkbox,
  FormHelperText,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { useTheme } from "@material-ui/styles";
import { useState, useEffect } from "react";
import { Theme } from "../../../theme";
import { Formik, FormikProps } from "formik";
import * as yup from "yup";
import { LoginInput, SignupInput } from "../AuthContainer";

const StyledSection = styled.section`
  ${({ theme }: { theme: Theme }) => {
    return css`
      width: 700px;
    
      .form{
          padding-left:100px;
          padding-right:100px;
          padding-bottom:125px;
          flex-basis: 700px;
          ${theme.breakpoints.down("sm")}{
              padding-left: ${theme.spacing(2)}px
              padding-right: ${theme.spacing(2)}px
          }
      }
      .title{
          margin-top: ${theme.spacing(3)}px
      }
      .textField{
          margin-top: ${theme.spacing(1.5)}px
      }
      .policy{
        margin-top: ${theme.spacing(1)}px
        display: flex;
        align-items:center;
      }
      .signUpButton{
          margin: ${theme.spacing(2, 0)}
      }
    `;
  }};
`;

const validationSchema = yup.object({
  name: yup.string().required("Name is required"),
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
  password: yup
    .string()
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d!$%@#£€*?&]{8,}$/,
      "Password must contain atleast 8 characters, one capital letter and one number"
    )
    .required("Enter your password"),
  // confirmPassword: yup
  //   .string("Enter your password")
  //   .required("Confirm your password")
  //   .oneOf([yup.ref("password")], "Password does not match"),
});

interface SignupValues {
  name: string;
  email: string;
  password: string;
  checked: boolean;
}

export interface SignupFormProps {
  goToSignin: () => void;
  handleSignup: (login: LoginInput, input: SignupInput) => void;
}

const SignupForm: React.FC<SignupFormProps> = props => {
  const theme: Theme = useTheme();

    //Get rid of any timer before unmount
    const [timer, setTimer] = useState<any>(undefined);
    useEffect(() => {
      return () => {
        if (timer) clearTimeout(timer);
      };
    }, []);

  return (
    <>
      <StyledSection theme={theme}>
        <div className="form">
          <Formik
            initialValues={{
              email: "",
              password: "",
              name: "",
              checked: false,
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              const { email, password, name } = values;
              await props.handleSignup(
                {
                  email,
                  password,
                },
                {
                  name,
                }
              );
              const timeout = setTimeout(() => {
                setSubmitting(false);
              }, 500);
              setTimer(timeout)
            }}
          >
            {({
              values,
              touched,
              errors,
              dirty,
              isSubmitting,
              handleChange,
              handleBlur,
              handleSubmit,
              handleReset,
              submitForm,
            }: FormikProps<SignupValues>) => (
              <>
                <Typography className="title" variant="h2">
                  Create new account
                </Typography>
                <Typography color="textSecondary" gutterBottom>
                  Use your email to create new account
                </Typography>
                <TextField
                  autoFocus
                  autoComplete="given-name"
                  className="textField"
                  error={!!(errors.name && touched.name)}
                  fullWidth
                  helperText={errors.name && touched.name && errors.name}
                  type="text"
                  label="Name"
                  name="name"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  variant="outlined"
                />
                <TextField
                  className="textField"
                  autoComplete="email"
                  error={!!(errors.email && touched.email)}
                  fullWidth
                  helperText={errors.email && touched.email && errors.email}
                  label="Email address"
                  name="email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type="text"
                  value={values.email}
                  variant="outlined"
                />
                <TextField
                  className="textField"
                  autoComplete="new-password"
                  error={!!(errors.password && touched.password)}
                  fullWidth
                  helperText={
                    errors.password && touched.password && errors.password
                  }
                  label="Password"
                  name="password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  type="password"
                  value={values.password}
                  variant="outlined"
                />
                <div className="policy">
                  <Checkbox
                    checked={values.checked}
                    name="checked"
                    className="policyCheckbox"
                    color="primary"
                    onChange={handleChange}
                  />
                  <Typography
                    className="policyText"
                    color="textSecondary"
                    variant="body1"
                  >
                    I have read the <Link to="#">Terms and Conditions</Link>
                  </Typography>
                </div>
                <Button
                  className="signUpButton"
                  color="primary"
                  disabled={
                    isSubmitting ||
                    !dirty ||
                    Object.keys(errors).length > 0 ||
                    !values.checked
                  }
                  onClick={submitForm}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                >
                  Sign up now
                </Button>
                <Typography
                  color="textSecondary"
                  variant="body1"
                  onClick={props.goToSignin}
                  style={{ cursor: "pointer" }}
                >
                  Have an account? Sign in
                </Typography>
              </>
            )}
          </Formik>
        </div>
      </StyledSection>
    </>
  );
};

export default SignupForm;
